import { BrandMark } from "../components/Brand";
import { PageMeta } from "../components/PageMeta";
import { useI18n } from "../i18n";

function LogoLockup({
  inverse = false,
  mono = false,
}: {
  inverse?: boolean;
  mono?: boolean;
}) {
  const { l } = useI18n();
  return (
    <div
      className={`logo-final-lockup ${inverse ? "logo-final-lockup--inverse" : ""} ${mono ? "logo-final-lockup--mono" : ""}`.trim()}
    >
      <BrandMark mono={mono} />
      <span>
        <strong>AccSci</strong>
        <small>{l("加速科学", "Acceleration Science")}</small>
      </span>
    </div>
  );
}

export function LogoPage() {
  const { l } = useI18n();
  return (
    <>
      <PageMeta title={l("品牌标志", "Brand mark")} />
      <section className="logo-page-hero">
        <div className="container logo-page-hero__grid">
          <p>{l("品牌标志 · 已选定", "BRAND MARK · SELECTED")}</p>
          <div>
            <h1>
              {l("破风，", "Cut through.")}
              <br />
              {l("成为标志。", "Leave a mark.")}
            </h1>
            <p>
              {l(
                "厚重的实心主体表达工程力量，宽阔的蓝色尾部积蓄动能，青色矛尖沿斜切缝向前突破。紧凑的几何共同构成抽象字母 A，也把“加速”变成一个可以被记住的动作。",
                "A solid frame signals engineering strength, a broad blue tail stores energy, and a cyan spearhead cuts forward through a diagonal channel. The compact geometry forms an abstract A and turns acceleration into a memorable action.",
              )}
            </p>
          </div>
        </div>
      </section>

      <section className="logo-final-section">
        <div className="container">
          <div className="logo-final-primary">
            <span>PRIMARY LOCKUP / 01</span>
            <LogoLockup />
          </div>

          <div className="logo-final-contexts">
            <figure className="logo-final-context logo-final-context--dark">
              <LogoLockup inverse />
              <figcaption>{l("深色反白", "Reversed on dark")}</figcaption>
            </figure>
            <figure className="logo-final-context logo-final-context--mono">
              <LogoLockup mono />
              <figcaption>{l("单色输出", "Monochrome")}</figcaption>
            </figure>
            <figure className="logo-final-context logo-final-context--sizes">
              <div>
                <span className="logo-final-size logo-final-size--64">
                  <BrandMark />
                </span>
                <span className="logo-final-size logo-final-size--32">
                  <BrandMark />
                </span>
                <span className="logo-final-size logo-final-size--20">
                  <BrandMark />
                </span>
              </div>
              <figcaption>64 / 32 / 20 px</figcaption>
            </figure>
          </div>

          <div className="logo-final-meta">
            <div>
              <span>{l("设计语言", "Design language")}</span>
              <p>
                {l(
                  "斜切、压缩、突破。没有发光、阴影或渐变，保留最直接的速度与力量。",
                  "Cut, compress, break through. No glow, shadow, or gradient—only direct speed and force.",
                )}
              </p>
            </div>
            <div>
              <span>{l("应用范围", "Applications")}</span>
              <p>
                {l(
                  "网站、设备铭牌、PCB 丝印、产品外壳、技术文档与浏览器图标。",
                  "Websites, device plates, PCB silkscreen, enclosures, technical documents, and browser icons.",
                )}
              </p>
            </div>
            <div>
              <span>{l("标准色", "Core colors")}</span>
              <p>#111418 · #2563EB · #00A8FF</p>
            </div>
          </div>

          <nav
            className="logo-downloads"
            aria-label={l("Logo 文件下载", "Logo downloads")}
          >
            <a href="/brand/accsci-logo.svg" download>
              {l("彩色组合 SVG", "Color lockup SVG")}
            </a>
            <a href="/brand/accsci-logo-inverse.svg" download>
              {l("反白组合 SVG", "Reverse lockup SVG")}
            </a>
            <a href="/brand/accsci-mark.svg" download>
              {l("彩色图标 SVG", "Color mark SVG")}
            </a>
            <a href="/brand/accsci-mark-mono.svg" download>
              {l("单色图标 SVG", "Mono mark SVG")}
            </a>
          </nav>
        </div>
      </section>

      <section className="logo-principles-section">
        <div className="container logo-principles-grid">
          <p>FORM LOGIC</p>
          <div>
            <h2>
              {l(
                "力量不是重量，速度也不只是箭头。",
                "Power is not weight. Speed is more than an arrow.",
              )}
            </h2>
            <ol>
              <li>
                <span>01</span>
                <div>
                  <strong>{l("承力骨架", "Load-bearing frame")}</strong>
                  <p>
                    {l(
                      "实心结构建立稳定重心，清晰的斜切缝让单色版本也保留破风轮廓。",
                      "The solid frame creates a stable center of gravity, while a clear diagonal channel preserves the silhouette in monochrome.",
                    )}
                  </p>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <strong>{l("压缩尾部", "Compressed tail")}</strong>
                  <p>
                    {l(
                      "蓝色短楔像被压缩的能量，在后端形成明确的起跑点。",
                      "The blue wedge stores compressed energy and creates a clear launch point.",
                    )}
                  </p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <strong>{l("破风矛尖", "Breakthrough spearhead")}</strong>
                  <p>
                    {l(
                      "青色主矢量穿过骨架并向前释放，把 AccSci 的行动感推向右上方。",
                      "The cyan vector cuts through the frame and releases forward, driving AccSci up and to the right.",
                    )}
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </section>
    </>
  );
}
